/*eslint complexity: ["error", 20]*/
import invariant from 'tiny-invariant'
import { useMemo } from 'react'
import * as R from 'remeda'
import {
  AttendeeId,
  Available,
  MeetingAttemptResponse,
  MeetingAttendees,
  UserAttendeeId,
} from '@chilipiper/api-client/src/user-availability'
import { PublicRoutingInfoForSelected } from '@chilipiper/api-client/src/handoff'
import { add, startOfDay } from '@chilipiper/date-time'
import {
  useFlexibleRoundRobinAvailabilities,
  useGetAvailabilitySpan,
  useUsersAvailabilities,
} from '@chilipiper/api-queries/src/user-availability'
import { OrderedListOfUsers, useGetOrderedListOfUsers } from '@chilipiper/distribution-components'
import { useMessageFeedContext } from '../../../chat-widget/MessageFeedContext'
import { getAssigneeOptions } from './helpers'
import { GetAvailabilityOptions } from './types'

const DEFAULT_INTERVAL_DURATION = '21 days'

const isAvailableMeetingAttempt = (
  meetingAttempt?: MeetingAttemptResponse
): meetingAttempt is Available => {
  return meetingAttempt?.type === 'Available'
}

export const useLoadMeetingAttemptRelatedData = ({
  now,
  handoffRouting,
  meetingAttempt,
  meetingTypeId,
  distribution,
  distributionId,
  isMeetingAttemptLoading,
  isRescheduling,
}: {
  meetingAttempt?: MeetingAttemptResponse
  meetingTypeId?: string
  distributionId?: string
  isMeetingAttemptLoading: boolean
  distribution?: OrderedListOfUsers
  handoffRouting?: PublicRoutingInfoForSelected
  now: Date
  isRescheduling: boolean
}) => {
  const { tenantId = '' } = useMessageFeedContext()

  invariant(meetingTypeId, 'meetingTypeId must be present')
  const availableMeetingAttempt = isAvailableMeetingAttempt(meetingAttempt)
    ? meetingAttempt
    : undefined

  const additionalRequiredAttendees = useMemo(
    () => getAdditionalRequiredAttendeesId(availableMeetingAttempt?.attempt.additionalAttendees),
    [availableMeetingAttempt?.attempt.additionalAttendees]
  )

  const availabilityOptions = getAssigneeOptions(
    availableMeetingAttempt?.attempt,
    handoffRouting,
    distribution,
    distributionId,
    isRescheduling
  )

  const {
    timeSlots,
    isLoading: isMeetingAttemptRelatedDataLoading,
    refetch: refetchGetAvailability,
  } = useGetMeetingAttemptAvailability({
    ...availabilityOptions,
    isMeetingAttemptLoading,
    additionalRequiredAttendees,
    tenantId,
    meetingTypeId,
    now,
    meetingDurationOverride: availableMeetingAttempt?.attempt.meetingInvite.meetingDurationOverride,
  })

  return {
    assigneeOptions: availabilityOptions,
    isLoading: isMeetingAttemptRelatedDataLoading,
    timeSlots,
    additionalRequiredAttendees,
    refetchAvailability: refetchGetAvailability,
  }
}

export const useLoadMeetingRelatedData = ({
  assignees,
  now,
  meetingTypeId,
  distributionId,
  isRescheduling,
}: {
  assignees: string[]
  meetingTypeId?: string
  now: Date
  distributionId: string | undefined
  isRescheduling: boolean
}) => {
  const { tenantId = '' } = useMessageFeedContext()

  invariant(meetingTypeId, 'meetingTypeId must be present')

  const {
    timeSlots,
    isLoading: isMeetingAttemptRelatedDataLoading,
    refetch: refetchGetAvailability,
    distribution,
  } = useGetMeetingRelatedDataAvailability({
    assignees,
    tenantId,
    meetingTypeId,
    now,
    distributionId,
    isRescheduling,
  })

  return {
    isLoading: isMeetingAttemptRelatedDataLoading,
    timeSlots,
    distribution,
    refetchAvailability: refetchGetAvailability,
  }
}

const getAdditionalRequiredAttendeesId = (attendees?: MeetingAttendees) => {
  if (!attendees || attendees.type === 'NoAdditionalAttendees') {
    return []
  }

  return attendees.attendees
    .filter(attendee => isInternalUser(attendee.id) && !attendee.optional)
    .map(attendee => (isInternalUser(attendee.id) ? attendee.id.userId : ''))
}

const isInternalUser = (attendeeId: AttendeeId): attendeeId is UserAttendeeId =>
  attendeeId.type === 'UserAttendeeId'

type IntervalOptions = {
  availabilityEndsAt: string
  intervalDuration?: string
  intervalStartsAt: string
}

const usePrepareAvailabilityPayload = (
  props: Omit<GetAvailabilityOptions, 'assigneeIds' | 'combinedAvailability'> &
    Partial<IntervalOptions> & { now: Date }
) => {
  const { tenantId, meetingTypeId, meetingDurationOverride, now } = props
  // todo: we never pass "meetingTypeVersion" so it's alwyas now.toISOString() which might introduce inconsistency
  const meetingTypeVersion = props.meetingTypeVersion ?? now.toISOString()

  const { data: availabilitySpanResponse, isLoading: isGetAvailabilitySpanLoading } =
    useGetAvailabilitySpan(tenantId, {
      meetingTypeId,
      meetingTypeVersion,
    })

  const availabilitySpan = useMemo(() => {
    if (!availabilitySpanResponse) {
      return
    }
    const endsAt = add(new Date(availabilitySpanResponse.startsAt), {
      days: 7,
    }).toISOString()

    return { startsAt: props.intervalStartsAt ?? availabilitySpanResponse.startsAt, endsAt }
  }, [availabilitySpanResponse?.startsAt, availabilitySpanResponse?.duration])

  return {
    payload: {
      meetingTypeRef: {
        id: meetingTypeId,
        timestamp: meetingTypeVersion,
      },
      meetingTypeOverride: {
        meetingDurationOverride: meetingDurationOverride,
        afterBufferDurationOverride: props.afterBufferDurationOverride,
        beforeBufferDurationOverride: props.beforeBufferDurationOverride,
      },
      interval: {
        duration: DEFAULT_INTERVAL_DURATION,
        startsAt: startOfDay(new Date(availabilitySpan?.startsAt ?? now)).toISOString(),
      },
      meetingLimit: {
        type: 'NoMeetingLimit' as const,
      },
    },
    isGetAvailabilitySpanLoading,
  }
}

const useGetMeetingRelatedDataAvailability = ({
  tenantId,
  meetingTypeId,
  assignees,
  now,
  distributionId,
  isRescheduling,
}: {
  assignees: string[]
  meetingTypeId: string
  now: Date
  tenantId: string
  distributionId: string | undefined
  isRescheduling: boolean
}) => {
  const { payload, isGetAvailabilitySpanLoading } = usePrepareAvailabilityPayload({
    tenantId,
    meetingTypeId,
    now,
  })

  const { data: distribution, isInitialLoading: isLoadingDistribution } = useGetOrderedListOfUsers(
    tenantId,
    distributionId
  )

  const isFlexibleRoundRobin = isRescheduling
    ? distribution?.reassignmentType === 'AnyTeamMember'
    : distribution?.roundRobinKind === 'Flexible'

  const {
    data: userFlexibleRoundRobinAvailabilities,
    isLoading: isFlexibleRoundRobinAvailabilitiesLoading,
    refetch: refetchFlexibleRoundRobinAvailabilities,
  } = useFlexibleRoundRobinAvailabilities(
    tenantId,
    {
      ...payload,
      expectedHost: {
        type: 'Assignee',
      },
      distributionId: distributionId!,
      assigneeIds: assignees,
    },
    {
      enabled: isFlexibleRoundRobin,
    }
  )
  const {
    data: userAvailabilities,
    isLoading: isUsersAvailabilitiesLoading,
    refetch: refetchUserAvailabilities,
  } = useUsersAvailabilities(
    tenantId,
    { ...payload, userIds: assignees, expectedHost: assignees[0] ?? '' },
    {
      enabled: !isFlexibleRoundRobin && !!assignees.length,
    }
  )

  if (isLoadingDistribution) {
    return {
      timeSlots: undefined,
      refetch: R.doNothing(),
      isLoading: true,
      distribution,
    }
  }

  // todo: we have recucursion here in booking-app if couldn't find any timeslots in selected interval
  return {
    timeSlots: isFlexibleRoundRobin ? userFlexibleRoundRobinAvailabilities : userAvailabilities,
    refetch: isFlexibleRoundRobin
      ? refetchFlexibleRoundRobinAvailabilities
      : refetchUserAvailabilities,
    isLoading: isFlexibleRoundRobin
      ? isFlexibleRoundRobinAvailabilitiesLoading && isGetAvailabilitySpanLoading
      : isUsersAvailabilitiesLoading,
    distribution,
  }
}

const useGetMeetingAttemptAvailability = (
  props: Pick<GetAvailabilityOptions, 'tenantId' | 'meetingTypeId'> &
    Partial<GetAvailabilityOptions> &
    Partial<IntervalOptions> & { now: Date; isMeetingAttemptLoading: boolean }
) => {
  const { payload, isGetAvailabilitySpanLoading } = usePrepareAvailabilityPayload(props)

  const isUsersAvailabilitiesEnabled =
    !!props.combinedAvailability && !props.isMeetingAttemptLoading

  const hostId = props?.combinedAvailability ? (props.hostId ?? '') : ''

  const {
    data: userAvailabilitiesResponse,
    isLoading: isUsersAvailabilitiesLoading,
    refetch: refetchUserAvailabilitiesResponse,
  } = useUsersAvailabilities(
    props.tenantId,
    {
      ...payload,
      expectedHost: hostId,
      userIds: [hostId, ...(props.additionalRequiredAttendees ?? [])].filter(userId => !!userId),
    },
    {
      enabled: isUsersAvailabilitiesEnabled,
    }
  )

  const isFlexibleRoundRobinAvailabilitiesEnabled =
    props.combinedAvailability === false && !props.isMeetingAttemptLoading && !!props.distributionId

  let assigneIds: string[] = []
  if (isFlexibleRoundRobinAvailabilitiesEnabled && 'assigneeIds' in props && props.assigneeIds) {
    assigneIds = props.assigneeIds
  }

  const {
    data: userFlexibleRoundRobinAvailabilities,
    isLoading: isFlexibleRoundRobinAvailabilitiesLoading,
    refetch: refetchFlexibleRoundRobinAvailabilities,
  } = useFlexibleRoundRobinAvailabilities(
    props.tenantId,
    {
      ...payload,
      expectedHost: {
        type: 'Assignee',
      },
      distributionId: isFlexibleRoundRobinAvailabilitiesEnabled ? props.distributionId! : '',
      assigneeIds: !props?.combinedAvailability ? assigneIds : [],
    },
    {
      enabled: isFlexibleRoundRobinAvailabilitiesEnabled,
    }
  )

  // todo: we have recucursion here in booking-app if couldn't find any timeslots in selected interval
  const timeSlots = props.combinedAvailability
    ? userAvailabilitiesResponse
    : userFlexibleRoundRobinAvailabilities

  const isAvailabilityLoading = props.combinedAvailability
    ? isUsersAvailabilitiesEnabled && isUsersAvailabilitiesLoading
    : isFlexibleRoundRobinAvailabilitiesEnabled && isFlexibleRoundRobinAvailabilitiesLoading

  return {
    timeSlots,
    refetch: props.combinedAvailability
      ? refetchUserAvailabilitiesResponse
      : refetchFlexibleRoundRobinAvailabilities,
    isLoading: isAvailabilityLoading && isGetAvailabilitySpanLoading,
  }
}
